/**
 * Pure form state for the brief intake modal.
 *
 * Owns nothing async, no side effects, no `useEffect`. The `isValid` flag
 * is derived during render from the current values; toggling fields
 * cannot get out of sync with validation.
 *
 * Consumers (`BriefIntakeModal`) read `values` for controlled inputs,
 * gate their submit button on `isValid`, and call `getSubmitPayload()` on
 * submit to hand the orchestrator (`useBriefGeneration.start`) a frozen
 * snapshot of the form contents PROJECTED to the BE shape (`artifactIds`
 * instead of the rich `artifacts` array kept on the FE).
 */
import { useCallback, useState } from 'react';

import type {
  BriefArtifactRef,
  BriefIntakeValues,
  BriefSubmitPayload,
  KnowledgeBaseRef,
} from '../brief-generation.types';

const INITIAL_VALUES: BriefIntakeValues = {
  templateId: null,
  templateName: null,
  title: '',
  instructions: '',
  files: [],
  artifacts: [],
  knowledgeBases: [],
};

export interface UseBriefIntakeFormReturn {
  /** Current form values. */
  values: BriefIntakeValues;
  /**
   * Derived during render — `true` when both `templateId` is set AND
   * `title` is non-empty after trimming.
   */
  isValid: boolean;
  setTemplate: (templateId: string | null, templateName: string | null) => void;
  setTitle: (title: string) => void;
  setInstructions: (instructions: string) => void;
  /** Append the given files to the current attached set (does not replace). */
  addFiles: (files: File[]) => void;
  /** Remove the file at the given index. */
  removeFile: (index: number) => void;
  /**
   * Merge the incoming artifact refs with the current selection,
   * deduplicating by `id`. The first occurrence wins (existing entries
   * are preserved unchanged).
   */
  addArtifacts: (refs: BriefArtifactRef[]) => void;
  /** Remove the artifact with the given id. No-op when the id is unknown. */
  removeArtifact: (id: string) => void;
  /**
   * Merge the incoming KB refs with the current selection,
   * deduplicating by `id`.
   */
  addKnowledgeBases: (refs: KnowledgeBaseRef[]) => void;
  /** Remove the knowledge base with the given id. */
  removeKnowledgeBase: (id: string) => void;
  /** Clear all selected knowledge bases. */
  clearKnowledgeBases: () => void;
  /** Reset all fields to the initial empty state. */
  reset: () => void;
  /**
   * Returns a frozen, BE-shaped snapshot of the current values. Throws
   * when the form is invalid (templateId still null) — callers MUST gate
   * the call on `isValid`.
   *
   * The returned object is detached from later mutations of the form
   * (arrays are copied) so the orchestrator can safely keep it across
   * the run.
   */
  getSubmitPayload: () => Readonly<BriefSubmitPayload>;
}

const dedupeById = <T extends { id: string }>(current: T[], incoming: T[]): T[] => {
  const seen = new Set(current.map((a) => a.id));
  const next = [...current];
  for (const ref of incoming) {
    if (seen.has(ref.id)) continue;
    seen.add(ref.id);
    next.push(ref);
  }
  return next;
};

/**
 * Stable identity for the common "nothing pre-attached" case, so a caller that
 * omits the argument never re-seeds on re-render.
 */
const NO_INITIAL_ARTIFACTS: BriefArtifactRef[] = [];

export const useBriefIntakeForm = (
  /**
   * Artifacts to start the form with, already attached. Read once, at mount:
   * the modal is unmounted while closed, so re-opening re-seeds naturally and a
   * later change to this argument cannot overwrite what the user has since
   * detached.
   */
  initialArtifacts: BriefArtifactRef[] = NO_INITIAL_ARTIFACTS,
): UseBriefIntakeFormReturn => {
  const [values, setValues] = useState<BriefIntakeValues>(() => ({
    ...INITIAL_VALUES,
    artifacts: initialArtifacts,
  }));

  // Derived during render — never sync via useEffect.
  const isValid =
    values.templateId !== null &&
    values.title.trim().length > 0 &&
    values.instructions.trim().length > 0;

  const setTemplate = useCallback(
    (templateId: string | null, templateName: string | null) =>
      setValues((v) => ({ ...v, templateId, templateName })),
    [],
  );

  const setTitle = useCallback((title: string) => setValues((v) => ({ ...v, title })), []);

  const setInstructions = useCallback(
    (instructions: string) => setValues((v) => ({ ...v, instructions })),
    [],
  );

  const addFiles = useCallback(
    (files: File[]) => setValues((v) => ({ ...v, files: [...v.files, ...files] })),
    [],
  );

  const removeFile = useCallback(
    (index: number) =>
      setValues((v) => ({
        ...v,
        files: v.files.filter((_, i) => i !== index),
      })),
    [],
  );

  const addArtifacts = useCallback(
    (refs: BriefArtifactRef[]) =>
      setValues((v) => ({ ...v, artifacts: dedupeById(v.artifacts, refs) })),
    [],
  );

  const removeArtifact = useCallback(
    (id: string) =>
      setValues((v) => ({
        ...v,
        artifacts: v.artifacts.filter((a) => a.id !== id),
      })),
    [],
  );

  const addKnowledgeBases = useCallback(
    (refs: KnowledgeBaseRef[]) =>
      setValues((v) => ({ ...v, knowledgeBases: dedupeById(v.knowledgeBases, refs) })),
    [],
  );

  const removeKnowledgeBase = useCallback(
    (id: string) =>
      setValues((v) => ({
        ...v,
        knowledgeBases: v.knowledgeBases.filter((kb) => kb.id !== id),
      })),
    [],
  );

  const clearKnowledgeBases = useCallback(
    () => setValues((v) => ({ ...v, knowledgeBases: [] })),
    [],
  );

  const reset = useCallback(() => setValues(INITIAL_VALUES), []);

  const getSubmitPayload = useCallback((): Readonly<BriefSubmitPayload> => {
    if (values.templateId === null || values.templateName === null) {
      throw new Error(
        'useBriefIntakeForm.getSubmitPayload: form is invalid (template is not selected)',
      );
    }
    return Object.freeze({
      templateId: values.templateId,
      templateName: values.templateName,
      title: values.title,
      instructions: values.instructions,
      files: [...values.files],
      artifactIds: values.artifacts.map((a) => a.id),
      knowledgeBaseIds: values.knowledgeBases.map((kb) => kb.id),
    });
  }, [values]);

  return {
    values,
    isValid,
    setTemplate,
    setTitle,
    setInstructions,
    addFiles,
    removeFile,
    addArtifacts,
    removeArtifact,
    addKnowledgeBases,
    removeKnowledgeBase,
    clearKnowledgeBases,
    reset,
    getSubmitPayload,
  };
};
